import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Role } from '../enums/roles.js';
import type { User } from './user.js';

// FIXME: user who can see the document are currently also able to change the share-permission
@Entity()
export class WorkshopDocument extends MongoEntity {
  public static override _class = 'WorkshopDocument';
  public static override _collectionName = 'workshop-documents';

  @Field(type => String)
  @validate(nonOptional())
  title!: string;

  @Field(type => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @Field(type => String)
  text?: string;

  @Field(type => String)
  fileID!: string;

  @Field(type => AdornisFile, {
    resolve: function (this: WorkshopDocument) {
      return async gqlFields => (await scopedCollection(AdornisFile._class)).findOne<AdornisFile>({ _id: this.fileID });
    },
  })
  file: Maybe<AdornisFile>;

  @validate(nonOptional())
  @Field(type => String)
  public workshopID!: string;

  @Field(type => Boolean, { default: v => v ?? false })
  sharedWithParticipantsWithSuccessfullParticipation: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  sharedWithParticipantsWithoutSuccessfullParticipation: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  sharedWithLecturers: Maybe<boolean>;

  override async create() {
    const { getMyself } = await import('@adornis/users/db/api/currentUserInfo.operations.js');
    // `isLecturer` is a getter over `roles`, not a selectable field — selecting `{ isLecturer: 1 }`
    // leaves `roles` unloaded, so the check stays false and a lecturer's own upload lands with
    // `sharedWithLecturers: false`, putting it outside their own publish filter so they can no longer
    // toggle its visibility. Request `roles` and derive the lecturer check from it directly.
    const currentUser = await getMyself<User>()({ roles: { name: 1 } });
    if (currentUser?.roles?.some(role => role.name === Role.LECTURER)) {
      this.sharedWithLecturers = true;
    }

    return super.create();
  }

  // Runs on the server after the document is saved (e.g. when an admin toggles a visibility flag in
  // the admin UI). Sends the "Neue Workshop-Dokumente verfügbar" mail to the participants the
  // document is now visible to. Deduped, so repeated saves don't re-send.
  static override async afterUpdate(oldEntity: WorkshopDocument, update: WorkshopDocument): Promise<void> {
    const documentID = (update as WorkshopDocument)?._id ?? oldEntity?._id;
    if (!documentID) return;

    try {
      const { notifyEligibleParticipantsForWorkshopDocument } =
        await import('../notification/new-workshop-document.js');
      await notifyEligibleParticipantsForWorkshopDocument(documentID);
    } catch (error) {
      logger.error({ error, documentID }, 'failed to trigger workshop document notifications on update');
    }
  }

  override toString() {
    return this.title ?? '';
  }
}
